package kpt.core.database.alerts

import androidx.room3.EntityInsertAdapter
import androidx.room3.RoomDatabase
import androidx.room3.coroutines.createFlow
import androidx.room3.util.getColumnIndexOrThrow
import androidx.room3.util.performSuspending
import androidx.sqlite.SQLiteStatement
import kotlin.Boolean
import kotlin.Double
import kotlin.Int
import kotlin.Long
import kotlin.String
import kotlin.Suppress
import kotlin.Unit
import kotlin.collections.List
import kotlin.collections.MutableList
import kotlin.collections.mutableListOf
import kotlin.reflect.KClass
import kotlinx.coroutines.flow.Flow

@Suppress(names = ["UNCHECKED_CAST", "DEPRECATION", "REDUNDANT_PROJECTION", "REMOVAL", "OPT_IN_USAGE_ERROR", "OPT_IN_USAGE", "MemberExtensionConflict", "CAN_BE_VAL"])
internal class AlertDao_Impl(
  __db: RoomDatabase,
) : AlertDao {
  private val __db: RoomDatabase

  private val __insertAdapterOfAlertEntity: EntityInsertAdapter<AlertEntity>
  init {
    this.__db = __db
    this.__insertAdapterOfAlertEntity = object : EntityInsertAdapter<AlertEntity>() {
      protected override fun createQuery(): String = "INSERT OR REPLACE INTO `alerts` (`id`,`symbol`,`targetPrice`,`conditionAbove`,`createdAt`) VALUES (?,?,?,?,?)"

      protected override fun bind(statement: SQLiteStatement, entity: AlertEntity) {
        statement.bindText(1, entity.id)
        statement.bindText(2, entity.symbol)
        statement.bindDouble(3, entity.targetPrice)
        val _tmp: Int = if (entity.conditionAbove) 1 else 0
        statement.bindLong(4, _tmp.toLong())
        statement.bindLong(5, entity.createdAt)
      }
    }
  }

  public override suspend fun upsert(alert: AlertEntity): Unit = performSuspending(__db, false, true) { _connection ->
    __insertAdapterOfAlertEntity.insert(_connection, alert)
  }

  public override suspend fun upsertAll(alerts: List<AlertEntity>): Unit = performSuspending(__db, false, true) { _connection ->
    __insertAdapterOfAlertEntity.insert(_connection, alerts)
  }

  public override fun observeAll(): Flow<List<AlertEntity>> {
    val _sql: String = "SELECT * FROM alerts ORDER BY createdAt DESC"
    return createFlow(__db, false, arrayOf("alerts")) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        val _columnIndexOfId: Int = getColumnIndexOrThrow(_stmt, "id")
        val _columnIndexOfSymbol: Int = getColumnIndexOrThrow(_stmt, "symbol")
        val _columnIndexOfTargetPrice: Int = getColumnIndexOrThrow(_stmt, "targetPrice")
        val _columnIndexOfConditionAbove: Int = getColumnIndexOrThrow(_stmt, "conditionAbove")
        val _columnIndexOfCreatedAt: Int = getColumnIndexOrThrow(_stmt, "createdAt")
        val _result: MutableList<AlertEntity> = mutableListOf()
        while (_stmt.step()) {
          val _item: AlertEntity
          val _tmpId: String
          _tmpId = _stmt.getText(_columnIndexOfId)
          val _tmpSymbol: String
          _tmpSymbol = _stmt.getText(_columnIndexOfSymbol)
          val _tmpTargetPrice: Double
          _tmpTargetPrice = _stmt.getDouble(_columnIndexOfTargetPrice)
          val _tmpConditionAbove: Boolean
          val _tmp: Int
          _tmp = _stmt.getLong(_columnIndexOfConditionAbove).toInt()
          _tmpConditionAbove = _tmp != 0
          val _tmpCreatedAt: Long
          _tmpCreatedAt = _stmt.getLong(_columnIndexOfCreatedAt)
          _item = AlertEntity(_tmpId,_tmpSymbol,_tmpTargetPrice,_tmpConditionAbove,_tmpCreatedAt)
          _result.add(_item)
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override fun observeById(id: String): Flow<AlertEntity?> {
    val _sql: String = "SELECT * FROM alerts WHERE id = ?"
    return createFlow(__db, false, arrayOf("alerts")) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, id)
        val _columnIndexOfId: Int = getColumnIndexOrThrow(_stmt, "id")
        val _columnIndexOfSymbol: Int = getColumnIndexOrThrow(_stmt, "symbol")
        val _columnIndexOfTargetPrice: Int = getColumnIndexOrThrow(_stmt, "targetPrice")
        val _columnIndexOfConditionAbove: Int = getColumnIndexOrThrow(_stmt, "conditionAbove")
        val _columnIndexOfCreatedAt: Int = getColumnIndexOrThrow(_stmt, "createdAt")
        val _result: AlertEntity?
        if (_stmt.step()) {
          val _tmpId: String
          _tmpId = _stmt.getText(_columnIndexOfId)
          val _tmpSymbol: String
          _tmpSymbol = _stmt.getText(_columnIndexOfSymbol)
          val _tmpTargetPrice: Double
          _tmpTargetPrice = _stmt.getDouble(_columnIndexOfTargetPrice)
          val _tmpConditionAbove: Boolean
          val _tmp: Int
          _tmp = _stmt.getLong(_columnIndexOfConditionAbove).toInt()
          _tmpConditionAbove = _tmp != 0
          val _tmpCreatedAt: Long
          _tmpCreatedAt = _stmt.getLong(_columnIndexOfCreatedAt)
          _result = AlertEntity(_tmpId,_tmpSymbol,_tmpTargetPrice,_tmpConditionAbove,_tmpCreatedAt)
        } else {
          _result = null
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun deleteById(id: String) {
    val _sql: String = "DELETE FROM alerts WHERE id = ?"
    return performSuspending(__db, false, true) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, id)
        _stmt.step()
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun deleteAll() {
    val _sql: String = "DELETE FROM alerts"
    return performSuspending(__db, false, true) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        _stmt.step()
      } finally {
        _stmt.close()
      }
    }
  }

  public companion object {
    public fun getRequiredColumnConverters(): List<KClass<*>> = emptyList()

    public fun getRequiredDaoReturnTypeConverters(): List<KClass<*>> = emptyList()
  }
}
